// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useNavigate, useSearch } from "@tanstack/react-router";
import type { PaginationState, SortingState } from "@tanstack/react-table";
import { Filter, ToolCase, UserRoundPlus, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/api/client";
import { type UserRow, useUsersQuery } from "@/api/hooks/use-users";
import { DataTable, type DataTableColumnDef } from "@/components/data-table";
import { PageHeader } from "@/components/layout/page-header";
import { DateTimeValue, DateValue } from "@/components/shared/datetime-value";
import { FilterPresets } from "@/components/shared/filter-presets";
import { FilterSelect } from "@/components/shared/filter-select";
import { FilterToggle } from "@/components/shared/filter-toggle";
import { RowId } from "@/components/shared/row-id";
import { StatusIndicator } from "@/components/shared/status-indicator";
import { StoreLink } from "@/components/shared/store-link";
import { TimezoneFooterNote } from "@/components/shared/timezone-footer-note";
import { UserLink } from "@/components/shared/user-link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Switch } from "@/components/ui/switch";
import { ChangeStoreDialog } from "@/features/iam/change-store-dialog";
import { CreateUserDialog } from "@/features/iam/create-user-dialog";
import { DeleteUserDialog } from "@/features/iam/delete-user-dialog";
import { LastLoginChartDialog } from "@/features/iam/last-login-chart-dialog";
import { RealmBadge } from "@/features/iam/realm-badge";
import { ToggleUserActiveDialog } from "@/features/iam/toggle-user-active-dialog";
import { UserActionsMenu } from "@/features/iam/user-actions-menu";
import { useBackofficeContext } from "@/hooks/use-backoffice-context";
import { useEscapeToClearFilters } from "@/hooks/use-escape-to-clear-filters";
import { useAuth } from "@/lib/auth/auth-provider";
import { PAGE_PERMISSIONS } from "@/lib/auth/page-permissions";
import { downloadCsv } from "@/lib/csv";
import { decodeFilters, encodeFilters } from "@/lib/filter-params";
import { getDefaultPageSize } from "@/lib/preferences";

const FILTER_DEFAULTS = {
  search: "",
  onlyActive: true,
  realm: "",
  chain: "",
  store: "",
  page: 0,
  get pageSize() {
    return getDefaultPageSize();
  },
  sortId: "userId",
  sortDesc: false,
};

export function UsersList({ group }: { group?: string } = {}) {
  const { t } = useTranslation("back-office");
  const { user: currentUser, hasPermission } = useAuth();
  const isAdmin = currentUser?.groups.includes("admin") ?? false;
  const canEdit = hasPermission(PAGE_PERMISSIONS.iamUsersEdit);
  const navigate = useNavigate();
  // When embedded under a group's Users tab (`group` set), the list is scoped to that group's
  // members, prefills the Create User dialog with it, and skips URL filter sync.
  const embedded = group !== undefined;
  const routeSearch = useSearch({ strict: false }) as { f?: string };
  const f = embedded ? undefined : routeSearch.f;
  const initial = useMemo(() => decodeFilters(f, FILTER_DEFAULTS), [f]);

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: initial.page,
    pageSize: initial.pageSize,
  });
  const [sorting, setSorting] = useState<SortingState>([{ id: initial.sortId, desc: initial.sortDesc }]);
  const [search, setSearch] = useState(initial.search);
  const [onlyActive, setOnlyActive] = useState(initial.onlyActive);
  const [realmFilter, setRealmFilter] = useState(initial.realm);
  const [chainFilter, setChainFilter] = useState(initial.chain);
  const [storeFilter, setStoreFilter] = useState(initial.store);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [lastLoginOpen, setLastLoginOpen] = useState(false);
  const [toggleUser, setToggleUser] = useState<UserRow | null>(null);
  const [deleteUser, setDeleteUser] = useState<UserRow | null>(null);
  const [changeStoreUser, setChangeStoreUser] = useState<{
    publicId: string;
    email: string;
    fullName?: string;
    defaultStoreName: string;
  } | null>(null);

  const { data: context, isLoading: contextLoading } = useBackofficeContext();

  // The single description of "what is filtered right now" — shared by the URL sync and the presets.
  const currentFilters = useMemo(
    () => ({
      search,
      onlyActive,
      realm: realmFilter,
      chain: chainFilter,
      store: storeFilter,
      page: pagination.pageIndex,
      pageSize: pagination.pageSize,
      sortId: sorting[0]?.id ?? "userId",
      sortDesc: sorting[0]?.desc ?? false,
    }),
    [search, onlyActive, realmFilter, chainFilter, storeFilter, pagination.pageIndex, pagination.pageSize, sorting],
  );

  const syncUrl = useCallback(
    (overrides: Partial<typeof FILTER_DEFAULTS> = {}) => {
      // Embedded under a group tab: don't write filter state to the URL (the route isn't /iam/users).
      if (embedded) return;
      const encoded = encodeFilters({ ...currentFilters, ...overrides }, FILTER_DEFAULTS);
      navigate({
        to: "/iam/users",
        search: encoded ? { f: encoded } : {},
        replace: true,
      });
    },
    [embedded, currentFilters, navigate],
  );

  const applyPreset = useCallback(function applyPreset(next: typeof FILTER_DEFAULTS) {
    setSearch(next.search);
    setOnlyActive(next.onlyActive);
    setRealmFilter(next.realm);
    setChainFilter(next.chain);
    setStoreFilter(next.store);
    setSorting([{ id: next.sortId, desc: next.sortDesc }]);
    // A preset carries no page index, and page size stays the viewer's own preference.
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  useEffect(
    function synchronizeFiltersToUrl() {
      syncUrl();
    },
    [syncUrl],
  );

  const sortBy = (sorting[0]?.id ?? "email") as "userId" | "email" | "fullName" | "active";
  const orderBy = sorting[0]?.desc ? ("desc" as const) : ("asc" as const);

  const isFiltered = !!(search || !onlyActive || realmFilter || chainFilter || storeFilter);

  const clearFilters = useCallback(function clearFilters() {
    setSearch("");
    setOnlyActive(true);
    setRealmFilter("");
    setChainFilter("");
    setStoreFilter("");
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  useEscapeToClearFilters({ isFiltered, clearFilters, disabled: toggleUser !== null });

  const { data, isLoading, refetch, isFetching } = useUsersQuery({
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    search: search || undefined,
    sortBy,
    orderBy,
    onlyActive: onlyActive || undefined,
    realm: realmFilter || undefined,
    chainName: chainFilter || undefined,
    storeName: storeFilter || undefined,
    group,
  });

  // Build a cumulative store map so inactive stores from previous responses remain available
  const storeMapRef = useRef(new Map<number, { storeName: string; legibleName: string }>());
  useMemo(() => {
    for (const s of data?.stores ?? []) {
      storeMapRef.current.set(s.storeId, { storeName: s.storeName, legibleName: s.legibleName });
    }
  }, [data?.stores]);
  const storeMap = storeMapRef.current;

  const handleExport = useCallback(async () => {
    const res = await api.GET("/v1/users", {
      params: {
        query: {
          page: 1,
          limit: -1,
          search: search || undefined,
          sortBy,
          orderBy,
          "filter[onlyActive]": onlyActive || undefined,
          "filter[realm]": realmFilter || undefined,
          "filter[chainName]": chainFilter || undefined,
          "filter[storeName]": storeFilter || undefined,
          "filter[group]": group || undefined,
        },
      },
    });
    const result = res.data as unknown as { users: UserRow[] };
    downloadCsv(
      "users.csv",
      ["ID", "Name", "Email", "Realm", "Status", "Last Login", "Created"],
      result.users.map((u) => [
        String(u.userId),
        u.fullName ?? "",
        u.email,
        u.realm,
        u.isActive ? "Active" : "Inactive",
        u.lastLoginAt ?? "",
        u.createdAt,
      ]),
    );
  }, [search, sortBy, orderBy, onlyActive, realmFilter, chainFilter, storeFilter, group]);

  const columns = useMemo<DataTableColumnDef<UserRow>[]>(
    () => [
      {
        accessorKey: "userId",
        header: t("users.list.columns.id"),
        size: 80,
        enableSorting: true,
        enableHiding: false,
        meta: { columnLabel: t("users.list.columns.id"), className: "pl-4", forceWidth: true, sticky: "left" },
        cell: ({ row }) => <RowId id={row.original.userId} />,
      },
      {
        accessorKey: "publicId",
        header: t("users.list.columns.publicId"),
        enableSorting: false,
        meta: { columnLabel: t("users.list.columns.publicId"), defaultHidden: true },
        cell: ({ row }) => <span className="font-mono">{row.original.publicId}</span>,
      },
      {
        accessorKey: "userName",
        header: t("users.list.columns.userName"),
        enableSorting: false,
        meta: { columnLabel: t("users.list.columns.userName"), defaultHidden: true },
        cell: ({ row }) => row.original.userName ?? <span className="text-muted-foreground">—</span>,
      },
      {
        accessorKey: "email",
        header: t("users.list.columns.email"),
        enableSorting: true,
        meta: { columnLabel: t("users.list.columns.email") },
        cell: ({ row, table }) => {
          // Hide the inline MFA badge / full-name sub-line when the dedicated column is on the user's view.
          const mfaColumnVisible = table.getColumn("hasMfa")?.getIsVisible() ?? false;
          const fullNameColumnVisible = table.getColumn("fullName")?.getIsVisible() ?? false;
          return (
            <div className="flex flex-col">
              <div className="flex items-center gap-2">
                <UserLink
                  publicId={row.original.publicId}
                  legibleName={row.original.email}
                  className="font-bold"
                  showIcon={false}
                />
                {row.original.hasMfa && !mfaColumnVisible && (
                  <Badge
                    variant="outline"
                    className="font-normal py-0 bg-green-100 text-green-700 border-green-200 dark:bg-green-950 dark:text-green-300 dark:border-green-900"
                  >
                    MFA
                  </Badge>
                )}
              </div>
              {row.original.fullName && !fullNameColumnVisible && (
                <span className="font-normal text-muted-foreground text-xs">{row.original.fullName}</span>
              )}
            </div>
          );
        },
      },
      {
        accessorKey: "fullName",
        header: t("users.list.columns.fullName"),
        enableSorting: false,
        meta: { columnLabel: t("users.list.columns.fullName"), defaultHidden: true },
        cell: ({ row }) => row.original.fullName || <span className="text-muted-foreground">—</span>,
      },
      {
        accessorKey: "defaultStoreId",
        header: t("users.list.columns.defaultStore"),
        enableSorting: false,
        meta: { columnLabel: t("users.list.columns.defaultStore") },
        cell: ({ row }) => {
          const storeId = row.original.defaultStoreId;
          if (!storeId) return <span className="text-muted-foreground">—</span>;
          const store = storeMap.get(storeId);
          if (!store) return storeId;
          return <StoreLink storeName={store.storeName} legibleName={store.legibleName} />;
        },
      },
      {
        accessorKey: "realm",
        header: t("users.list.columns.realm"),
        size: 120,
        enableSorting: false,
        meta: { columnLabel: t("users.list.columns.realm") },
        cell: ({ row }) => <RealmBadge realm={row.original.realm} />,
      },
      {
        accessorKey: "hasMfa",
        header: t("users.list.columns.mfa"),
        size: 80,
        enableSorting: false,
        meta: { columnLabel: t("users.list.columns.mfa"), defaultHidden: true, forceWidth: true },
        cell: ({ row }) => (
          <Switch
            checked={row.original.hasMfa}
            tabIndex={-1}
            aria-readonly
            aria-label={t("users.list.columns.mfa")}
            className="pointer-events-none"
          />
        ),
      },
      {
        accessorKey: "lastLoginAt",
        header: t("users.list.columns.lastLoginAt"),
        enableSorting: false,
        meta: { columnLabel: t("users.list.columns.lastLoginAt") },
        cell: ({ row }) => <DateTimeValue value={row.original.lastLoginAt} />,
      },
      {
        accessorKey: "lastAttemptAt",
        header: t("users.list.columns.lastAttemptAt"),
        enableSorting: false,
        meta: { columnLabel: t("users.list.columns.lastAttemptAt"), defaultHidden: true },
        cell: ({ row }) => <DateTimeValue value={row.original.lastAttemptAt} />,
      },
      {
        accessorKey: "createdAt",
        header: t("users.list.columns.createdAt"),
        enableSorting: false,
        meta: { columnLabel: t("users.list.columns.createdAt") },
        cell: ({ row }) => <DateValue value={row.original.createdAt} />,
      },
      {
        accessorKey: "isActive",
        header: t("users.list.columns.status"),
        size: 80,
        enableSorting: false,
        meta: { columnLabel: t("users.list.columns.status") },
        cell: ({ row }) => <StatusIndicator active={row.original.isActive} deleted={row.original.isDeleted} />,
      },
      {
        id: "actions",
        size: 50,
        meta: { forceWidth: true, sticky: "right" },
        enableSorting: false,
        enableHiding: false,
        cell: ({ row }) =>
          row.original.isDeleted ? null : (
            <UserActionsMenu
              isActive={row.original.isActive}
              isAdmin={isAdmin}
              isSelf={currentUser?.publicId === row.original.publicId}
              onChangeStore={() => {
                const store = row.original.defaultStoreId ? storeMap.get(row.original.defaultStoreId) : undefined;
                setChangeStoreUser({
                  publicId: row.original.publicId,
                  email: row.original.email,
                  fullName: row.original.fullName,
                  defaultStoreName: store?.storeName ?? "",
                });
              }}
              onToggleActive={() => setToggleUser(row.original)}
              onDelete={() => setDeleteUser(row.original)}
            />
          ),
      },
    ],
    [t, storeMap, isAdmin, currentUser?.publicId],
  );

  return (
    <div>
      {!embedded && <PageHeader title={t("users.title")} />}
      <div className="p-6">
        <DataTable
          storageKey={embedded ? "group-users-list" : "users-list"}
          columns={columns}
          data={data?.users ?? []}
          totalCount={data?.count ?? 0}
          isLoading={isLoading}
          pagination={pagination}
          onPaginationChange={setPagination}
          sorting={sorting}
          onSortingChange={setSorting}
          search={search}
          onSearchChange={setSearch}
          searchDebounceMs={500}
          onRefresh={refetch}
          isRefreshing={isFetching}
          onExport={handleExport}
          searchPlaceholder={t("users.list.searchPlaceholder")}
          presets={
            embedded ? undefined : (
              <FilterPresets
                storageKey="users-list"
                defaults={FILTER_DEFAULTS}
                current={currentFilters}
                onApply={applyPreset}
              />
            )
          }
          filters={
            <>
              <FilterSelect
                label={t("users.list.filter.realm")}
                value={realmFilter}
                onChange={(v) => {
                  setRealmFilter(v);
                  setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                }}
                options={[...(context?.userRealms ?? [])]
                  .sort((a, b) => {
                    if (a === "local") return -1;
                    if (b === "local") return 1;
                    return a.localeCompare(b);
                  })
                  .map((r) => ({ label: r, value: r }))}
                searchPlaceholder={t("users.list.filter.searchRealms")}
                emptyText={t("users.list.filter.noRealms")}
                loading={contextLoading}
              />
              <FilterSelect
                label={t("users.list.filter.chain")}
                value={chainFilter}
                onChange={(v) => {
                  setChainFilter(v);
                  setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                }}
                options={(context?.chains ?? []).map((c) => ({ label: c.display, value: c.value }))}
                searchPlaceholder={t("users.list.filter.searchChains")}
                emptyText={t("users.list.filter.noChains")}
                loading={contextLoading}
              />
              <FilterSelect
                label={t("users.list.filter.store")}
                value={storeFilter}
                onChange={(v) => {
                  setStoreFilter(v);
                  setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                }}
                options={(context?.stores ?? []).map((s) => ({ label: s.display, value: s.value }))}
                searchPlaceholder={t("users.list.filter.searchStores")}
                emptyText={t("users.list.filter.noStores")}
                loading={contextLoading}
              />
              <FilterToggle
                active={onlyActive}
                onChange={() => {
                  setOnlyActive((prev) => !prev);
                  setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                }}
                icon={Filter}
                label={t("common.active")}
              />
              {isFiltered && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 text-muted-foreground hover:text-foreground"
                  onClick={clearFilters}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </>
          }
          toolbar={
            <>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="icon" aria-label={t("users.tools.menuLabel")}>
                    <ToolCase className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" sideOffset={-36} className="dropdown-overlap">
                  <DropdownMenuLabel className="flex items-center gap-1.5 text-xs font-normal uppercase tracking-wider text-muted-foreground">
                    <ToolCase className="h-3.5 w-3.5" />
                    {t("users.tools.menuLabel")}
                  </DropdownMenuLabel>
                  <DropdownMenuItem onSelect={() => setLastLoginOpen(true)}>
                    {t("users.tools.lastLogin")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              {canEdit && (
                <Button onClick={() => setCreateDialogOpen(true)}>
                  <UserRoundPlus className="h-4 w-4" />
                  {t("users.list.addUserButton")}
                </Button>
              )}
            </>
          }
          footerNote={<TimezoneFooterNote />}
        />
      </div>

      <CreateUserDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        defaultGroups={group ? [group] : undefined}
      />
      <LastLoginChartDialog
        open={lastLoginOpen}
        onOpenChange={setLastLoginOpen}
        filters={{
          search: search || undefined,
          onlyActive: onlyActive || undefined,
          realm: realmFilter || undefined,
          chainName: chainFilter || undefined,
          storeName: storeFilter || undefined,
          group,
        }}
      />
      <ChangeStoreDialog user={changeStoreUser} onClose={() => setChangeStoreUser(null)} />
      <ToggleUserActiveDialog user={toggleUser} onClose={() => setToggleUser(null)} />
      <DeleteUserDialog user={deleteUser} onClose={() => setDeleteUser(null)} />
    </div>
  );
}
